/**
 * Omega Yeast (Chicago) — catalogued in full from omegayeast.com: the standard
 * strains, the Brett and bacteria cultures, the engineered Thiolized, Lumina,
 * DKO and Zing strains, and the Pinnacle dry yeasts Omega distributes.
 *
 * Temperatures are °C as Omega publishes them alongside °F (PLAN §5 canon).
 * Attenuation is the numeric band behind Omega's word — "Medium: 76-82%"
 * reads as 76-82 — and `alcoholTolerancePercent` keeps the upper end of the
 * band, matching the White Labs convention.
 *
 * Omega prints a Diastatic result on every strain page, so `sta1` here is a
 * published test result: `true` for the STA1-positive strains, `false` where
 * Omega tested negative.
 *
 * The two bacteria (OYL-605, OYL-606) carry no published attenuation,
 * temperature or flocculation — Omega gives their temperature in prose only —
 * so those two entries are `representative`. OYL-410 Solsta and PIN-005DRY
 * publish no figures at all and are deliberately absent.
 */

import type { Yeast } from '../../schema/yeast';

/** Omega Yeast cultures. @see Yeast */
export const omegaYeasts: readonly Yeast[] = [
  {
    id: 'omega-oyl-001',
    lab: 'Omega Yeast',
    productCode: 'OYL-001',
    name: 'Alt',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Düsseldorf altbier strain: clean and low in ester, with quick maturation and next to no diacetyl. It is the shortest route to a German brown or amber that tastes German.',
    tags: ['alt', 'german', 'dusseldorf', 'clean', 'low-ester', 'quick'],
    url: 'https://omegayeast.com/products/alt-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-002',
    lab: 'Omega Yeast',
    productCode: 'OYL-002',
    name: 'American Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An energetic top cropper for American wheat beers, leaving a light, tart, crisp beer and very little of itself. Reach for it when the wheat and the hops should carry the glass.',
    tags: ['american', 'wheat', 'tart', 'crisp', 'top-cropping', 'neutral'],
    url: 'https://omegayeast.com/products/american-wheat-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-003',
    lab: 'Omega Yeast',
    productCode: 'OYL-003',
    name: 'London Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A London strain that attenuates hard for its family, with pronounced minerality and mild fruit. It suits dry English bitters where WLP002 would leave too much sweetness.',
    tags: ['english', 'london', 'mineral', 'attenuative', 'dry', 'bitter'],
    url: 'https://omegayeast.com/products/london-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-004',
    lab: 'Omega Yeast',
    productCode: 'OYL-004',
    name: 'West Coast I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The Chico workhorse in liquid form: clean, neutral and forgiving, leaving hops and malt to do the talking. Ferment it cool for a crisp pale ale, warm for a rounder body, and expect it to drop reasonably bright without stripping character.',
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'west-coast'],
    url: 'https://omegayeast.com/products/west-coast-ale-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-005',
    lab: 'Omega Yeast',
    productCode: 'OYL-005',
    name: 'Irish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A slightly tart, dry-finishing Irish strain that lets roast and crystal malt speak instead of layering fruit over them. Ferment cool for a dry stout, a little warmer if you want diacetyl and body in a sweeter one.',
    tags: ['irish', 'stout', 'porter', 'roast', 'dry', 'malty'],
    url: 'https://omegayeast.com/products/irish-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-006',
    lab: 'Omega Yeast',
    productCode: 'OYL-006',
    name: 'British Ale I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Two beers in one strain: clean and crisp on its own, and reliably hazy once you dry hop it. Genetically haze-positive, and flocculent enough to clear if you leave the hops out.',
    tags: ['english', 'london', 'fruity', 'hazy', 'ipa', 'flocculent', 'dual-purpose'],
    url: 'https://omegayeast.com/products/british-ale-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-007',
    lab: 'Omega Yeast',
    productCode: 'OYL-007',
    name: 'British Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Sister strain to British Ale I from the same London brewery, with stronger fruit esters and more malt presence. The choice when a bitter should taste of yeast as much as of malt.',
    tags: ['english', 'london', 'fruity', 'malty', 'esters', 'flocculent'],
    url: 'https://omegayeast.com/products/british-ale-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-008',
    lab: 'Omega Yeast',
    productCode: 'OYL-008',
    name: 'British Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Complex, malty and highly flocculent, and a notoriously slow starter. It clears beautifully if you give it the extra days it asks for rather than cold-crashing on schedule.',
    tags: ['english', 'malty', 'complex', 'flocculent', 'slow', 'patient'],
    url: 'https://omegayeast.com/products/british-ale-iii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-009',
    lab: 'Omega Yeast',
    productCode: 'OYL-009',
    name: 'West Coast II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A West Coast strain that is genetically haze-positive: dry hopping triggers stable haze over a drier body than West Coast I. Fruit, citrus and hops get room that a clean strain does not give them.',
    tags: ['american', 'west-coast', 'hazy', 'dry', 'citrus', 'ipa'],
    url: 'https://omegayeast.com/products/west-coast-ale-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-010',
    lab: 'Omega Yeast',
    productCode: 'OYL-010',
    name: 'British Ale IV',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Apple, pear and clover honey over a light mineral finish, which suits English styles and hop-forward beers alike. One of the more aromatic British strains in the range.',
    tags: ['english', 'apple', 'pear', 'honey', 'mineral', 'versatile'],
    url: 'https://omegayeast.com/products/british-ale-iv-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-011',
    lab: 'Omega Yeast',
    productCode: 'OYL-011',
    name: 'British Ale V',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The standard bearer for hazy IPA: fruity esters and residual sweetness that amplify juicy hop character. It flocculates well for a haze strain, which is part of why it is so widely used.',
    tags: ['english', 'fruity', 'residual-sweetness', 'hazy', 'neipa', 'flocculent'],
    url: 'https://omegayeast.com/products/british-ale-v-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-012',
    lab: 'Omega Yeast',
    productCode: 'OYL-012',
    name: 'Pacific NW Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A Pacific Northwest strain with British roots and a fairly neutral profile, adding malt and light fruit without taking over. It flocculates well, so it clears without help.',
    tags: ['american', 'pacific-northwest', 'neutral', 'malty', 'flocculent'],
    url: 'https://omegayeast.com/products/pacific-nw-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-013',
    lab: 'Omega Yeast',
    productCode: 'OYL-013',
    name: 'British Ale VI',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Classic British character with the alcohol tolerance for a strong ale: crisp, clean and malty with a mostly dry finish. A reliable choice when the gravity climbs.',
    tags: ['english', 'crisp', 'malty', 'dry', 'high-gravity', 'flocculent'],
    url: 'https://omegayeast.com/products/british-ale-vi-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-014',
    lab: 'Omega Yeast',
    productCode: 'OYL-014',
    name: 'British Ale VII',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A well-behaved high flocculator giving clear, malty ales with stone-fruit esters and a dry, nutty finish. It drops fast, which shortens the wait for a bright beer.',
    tags: ['english', 'stone-fruit', 'nutty', 'flocculent', 'malty', 'clear'],
    url: 'https://omegayeast.com/products/british-ale-vii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-015',
    lab: 'Omega Yeast',
    productCode: 'OYL-015',
    name: 'Scottish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A flocculent Scottish strain whose malt complexity slides with temperature: push it warm and the esters follow. Late dry hopping turns it haze-positive.',
    tags: ['scottish', 'malty', 'flocculent', 'hop-friendly', 'hazy', 'esters'],
    url: 'https://omegayeast.com/products/scottish-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-016',
    lab: 'Omega Yeast',
    productCode: 'OYL-016',
    name: 'Extra Special',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Thought to come from a well-regarded English ESB, and it flocculates as thoroughly as that suggests. It drops fast and completely, so rouse it if the gravity stalls early.',
    tags: ['english', 'esb', 'flocculent', 'drops-fast', 'malty', 'bitter'],
    url: 'https://omegayeast.com/products/extra-special-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-017',
    lab: 'Omega Yeast',
    productCode: 'OYL-017',
    name: 'Kolsch I',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Lightly fruity esters over a crisp, lager-like backbone, which is what a kölsch is for. An enthusiastic top cropper, so leave headspace.',
    tags: ['kolsch', 'german', 'crisp', 'lager-like', 'top-cropping', 'hazy'],
    url: 'https://omegayeast.com/products/kolsch-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-018',
    lab: 'Omega Yeast',
    productCode: 'OYL-018',
    name: 'Abbey Ale C',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A Trappist strain that rewards patience: low flocculation, fruity and lightly spiced, and often slow to finish. Give it warmth and time rather than a second pitch.',
    tags: ['belgian', 'trappist', 'banana', 'spicy', 'low-floc', 'patient', 'high-gravity'],
    url: 'https://omegayeast.com/products/abbey-ale-c-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-019',
    lab: 'Omega Yeast',
    productCode: 'OYL-019',
    name: 'Belgian Ale D',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'Rich and complex, and demanding: it ferments in fits and starts and can take weeks to finish. It carries STA1 and attenuates past 82%, so plan for a very dry beer.',
    tags: ['belgian', 'attenuative', 'spicy', 'dry', 'sta1', 'stop-start', 'complex'],
    url: 'https://omegayeast.com/products/belgian-ale-d-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-020',
    lab: 'Omega Yeast',
    productCode: 'OYL-020',
    name: 'Belgian Ale R',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Unusually low in phenol for a Belgian strain, giving stone fruit and a light floral or rose note instead. Useful when a Belgian beer should be fruity rather than spicy.',
    tags: ['belgian', 'stone-fruit', 'rose', 'floral', 'low-phenolic', 'flocculent'],
    url: 'https://omegayeast.com/products/belgian-ale-r-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-021',
    lab: 'Omega Yeast',
    productCode: 'OYL-021',
    name: 'Hefeweizen I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Weihenstephan-type weizen: banana ester against clove phenol, the balance shifting with pitch rate and temperature. It stays in suspension by design — do not fine a hefeweizen brewed with it.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'phenolic', 'german'],
    url: 'https://omegayeast.com/products/hefeweizen-ale-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-022',
    lab: 'Omega Yeast',
    productCode: 'OYL-022',
    name: 'Hefeweizen II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A second German wheat option with a slightly different ester-to-phenol balance and better flocculation than Hefeweizen I. A good alternative when the banana needs reining in.',
    tags: ['wheat', 'hefeweizen', 'banana', 'clove', 'german', 'balanced'],
    url: 'https://omegayeast.com/products/hefeweizen-ale-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-023',
    lab: 'Omega Yeast',
    productCode: 'OYL-023',
    name: 'Grand Cru',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The Hoegaarden wheat strain: clove phenolics, esters, and a tart, dry finish. It is the reference point for witbier and works for grand cru as well.',
    tags: ['witbier', 'belgian', 'hoegaarden', 'clove', 'tart', 'dry', 'grand-cru'],
    url: 'https://omegayeast.com/products/grand-cru-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-024',
    lab: 'Omega Yeast',
    productCode: 'OYL-024',
    name: 'Belgian Ale A',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'One of the few Belgian strains that crops easily, with well-rounded fruitiness and balanced phenolics. A reliable house Belgian when you plan to repitch.',
    tags: ['belgian', 'spicy', 'fruity', 'croppable', 'high-gravity', 'balanced'],
    url: 'https://omegayeast.com/products/belgian-ale-a-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-025',
    lab: 'Omega Yeast',
    productCode: 'OYL-025',
    name: 'Bavarian Wheat I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'A Bavarian wheat strain producing banana and clove, close to Hefeweizen I with subtle differences. It carries STA1 and attenuates past 82%, so bottle-conditioned weizen wants watching.',
    tags: ['wheat', 'bavarian', 'banana', 'clove', 'sta1', 'attenuative'],
    url: 'https://omegayeast.com/products/bavarian-wheat-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-026',
    lab: 'Omega Yeast',
    productCode: 'OYL-026',
    name: 'French Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'A relentlessly attenuative saison: pepper, lemon zest and a finish that will keep chewing in package. Diastatic — treat shared equipment as contaminated and do not bottle until gravity has actually stopped.',
    tags: ['saison', 'french', 'farmhouse', 'phenolic', 'diastatic', 'dry-finish'],
    url: 'https://omegayeast.com/products/french-saison-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-027',
    lab: 'Omega Yeast',
    productCode: 'OYL-027',
    name: 'Belgian Saison I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'A well-regarded farmhouse strain with fruity complexity, noticeable phenolics and a tart, dry finish. It carries STA1 and can stall partway before finishing if you keep it warm.',
    tags: ['saison', 'belgian', 'farmhouse', 'phenolic', 'tart', 'dry', 'sta1'],
    url: 'https://omegayeast.com/products/belgian-saison-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-028',
    lab: 'Omega Yeast',
    productCode: 'OYL-028',
    name: 'Belgian Ale W',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'An eruptive top cropper with bright fruit and rustic phenolics, and reliable flocculation for a Belgian strain. It works across a wide temperature range without losing its balance.',
    tags: ['belgian', 'top-cropping', 'fruity', 'phenolic', 'rustic', 'flocculent'],
    url: 'https://omegayeast.com/products/belgian-ale-w-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-029',
    lab: 'Omega Yeast',
    productCode: 'OYL-029',
    name: 'Belgian Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'More ester complexity than either hefeweizen strain: apple, bubblegum and plum over a lightly tart, dry finish. A Belgian answer to the German wheat yeasts.',
    tags: ['belgian', 'wheat', 'apple', 'bubblegum', 'plum', 'tart', 'dry'],
    url: 'https://omegayeast.com/products/belgian-wheat-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-030',
    lab: 'Omega Yeast',
    productCode: 'OYL-030',
    name: 'Wit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The essential wit strain, with spicy phenolics carrying the flavor and solid ester behind them. The light tartness is what keeps coriander and orange peel from turning cloying.',
    tags: ['witbier', 'belgian', 'spicy', 'phenolic', 'tart', 'coriander'],
    url: 'https://omegayeast.com/products/wit-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-032',
    lab: 'Omega Yeast',
    productCode: 'OYL-032',
    name: 'East Coast Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An East Coast strain with a slight tartness that can mute hop bitterness, and less flocculation and attenuation than the West Coast strains. Dry hopping promotes haze.',
    tags: ['american', 'east-coast', 'tart', 'neutral', 'hazy', 'versatile'],
    url: 'https://omegayeast.com/products/east-coast-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-033',
    lab: 'Omega Yeast',
    productCode: 'OYL-033',
    name: 'Jovaru Lithuanian Farmhouse',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'The Jovaru strain from the queen of Lithuanian farmhouse beer, with citrus esters, restrained phenols and lemon pith. It carries STA1 and attenuates as far as a saison strain.',
    tags: ['farmhouse', 'lithuanian', 'jovaru', 'citrus', 'lemon', 'sta1', 'attenuative'],
    url: 'https://omegayeast.com/products/jovaru-lithuanian-farmhouse-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-034',
    lab: 'Omega Yeast',
    productCode: 'OYL-034',
    name: 'Bavarian Wheat II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A complex Bavarian wheat strain with banana, pear, apple, plum, clove and vanilla in play at once. More aromatic than Hefeweizen I, and worth the extra attention to pitch rate.',
    tags: ['wheat', 'bavarian', 'banana', 'pear', 'apple', 'clove', 'vanilla'],
    url: 'https://omegayeast.com/products/bavarian-wheat-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-039',
    lab: 'Omega Yeast',
    productCode: 'OYL-039',
    name: 'Biere de Garde',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'A bière de garde strain that presents like a saison: lightly phenolic, fruity, delicately tart, and very slow to drop. It carries STA1 and attenuates deeply.',
    tags: ['biere-de-garde', 'phenolic', 'fruity', 'tart', 'low-floc', 'sta1'],
    url: 'https://omegayeast.com/products/biere-de-garde-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-042',
    lab: 'Omega Yeast',
    productCode: 'OYL-042',
    name: 'Belgian Saison II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'Thought to come from a small Belgian spelt saison: earthy, spicy and peppery with tropical fruit behind it. It carries STA1 and finishes very dry.',
    tags: ['saison', 'belgian', 'spelt', 'peppery', 'earthy', 'tropical', 'sta1'],
    url: 'https://omegayeast.com/products/belgian-saison-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-043',
    lab: 'Omega Yeast',
    productCode: 'OYL-043',
    name: 'Point Loma',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A neutral West Coast strain that stays out of the way and flocculates hard, with suspiciously low diacetyl for how fast it works. Haze-positive if you dry hop it late.',
    tags: ['american', 'west-coast', 'neutral', 'flocculent', 'hazy', 'ipa', 'low-diacetyl'],
    url: 'https://omegayeast.com/products/point-loma-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-044',
    lab: 'Omega Yeast',
    productCode: 'OYL-044',
    name: 'Kolsch II',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Lightly fruity, crisp and hop-friendly, and it flocculates like it has somewhere to be. Late dry hopping turns it haze-positive, so it covers both a clean kölsch and a pale hazy beer.',
    tags: ['kolsch', 'german', 'fruity', 'crisp', 'hop-friendly', 'flocculent', 'hazy'],
    url: 'https://omegayeast.com/products/kolsch-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-046',
    lab: 'Omega Yeast',
    productCode: 'OYL-046',
    name: 'Belgian Ale O',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'A Trappist strain with balanced fruit and phenolics over a clean dry profile, and a signature sharp, slightly acidic edge. It gives a big Belgian beer somewhere to finish.',
    tags: ['belgian', 'trappist', 'fruity', 'phenolic', 'dry', 'acidic'],
    url: 'https://omegayeast.com/products/belgian-ale-o-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-049',
    lab: 'Omega Yeast',
    productCode: 'OYL-049',
    name: 'Belgian Ale DK',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Mild Belgian character over light toasty, biscuit-like maltiness. It can throw a lagery sulfur note early that conditions out, so do not judge it at packaging.',
    tags: ['belgian', 'toasty', 'biscuit', 'malty', 'sulfur', 'mild'],
    url: 'https://omegayeast.com/products/belgian-ale-dk-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-050',
    lab: 'Omega Yeast',
    productCode: 'OYL-050',
    name: 'West Coast IV',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A neutral, productive workhorse for clean American ales, with mild fruity esters across its range. It flocculates better than the Chico strain, which is often the reason to pick it.',
    tags: ['american', 'west-coast', 'neutral', 'clean', 'flocculent', 'workhorse'],
    url: 'https://omegayeast.com/products/west-coast-ale-iv-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-052',
    lab: 'Omega Yeast',
    productCode: 'OYL-052',
    name: 'DIPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Known as Conan: peach, apricot and pineapple that act as steroids for modern fruity hop profiles. Give it a diacetyl rest if you ferment at the cool end of the range.',
    tags: ['conan', 'peach', 'apricot', 'pineapple', 'neipa', 'ipa', 'diacetyl-rest'],
    url: 'https://omegayeast.com/products/dipa-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-056',
    lab: 'Omega Yeast',
    productCode: 'OYL-056',
    name: 'Belgian Golden Strong',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'Built for high-gravity Belgian ales with a bone-dry finish, with light banana and pear over mellow phenols. It carries STA1, which is how it gets that dry.',
    tags: ['belgian', 'golden-strong', 'dry', 'banana', 'pear', 'sta1', 'high-gravity'],
    url: 'https://omegayeast.com/products/belgian-golden-strong-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-057',
    lab: 'Omega Yeast',
    productCode: 'OYL-057',
    name: 'Hothead',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'A highly flocculent Norwegian kveik with an astounding temperature range and a consistent flavor across all of it. Clean enough to pass for an American ale strain when you ferment it cool.',
    tags: ['kveik', 'hothead', 'norwegian', 'fast', 'hot', 'flocculent', 'clean'],
    url: 'https://omegayeast.com/products/hothead-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-061',
    lab: 'Omega Yeast',
    productCode: 'OYL-061',
    name: 'Voss',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'The Voss kveik: citrus aromatics that get louder as you push the temperature, on a very fast timeline. Genetically haze-positive, which suits the hoppy beers it usually goes into.',
    tags: ['kveik', 'voss', 'norwegian', 'citrus', 'fast', 'hazy', 'hot'],
    url: 'https://omegayeast.com/products/voss-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-071',
    lab: 'Omega Yeast',
    productCode: 'OYL-071',
    name: 'Lutra',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 12, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'The unusually neutral kveik — it keeps the speed and heat tolerance but drops most of the farmhouse esters, so warm-fermented beer tastes close to a clean lager or pale ale. The obvious pick for pseudo-lagers brewed without a glycol chiller.',
    tags: ['kveik', 'neat', 'clean', 'pseudo-lager', 'fast', 'norwegian'],
    url: 'https://omegayeast.com/products/lutra-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-071dry',
    lab: 'Omega Yeast',
    productCode: 'OYL-071DRY',
    name: 'Dried Lutra',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 12, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'Lutra in dry form, with the same clean profile and enormous temperature range as the liquid pitch. Useful when you want a kveik on the shelf rather than in the fridge.',
    tags: ['kveik', 'lutra', 'dry', 'clean', 'fast', 'versatile', 'wide-range'],
    url: 'https://omegayeast.com/products/dried-lutra-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-090',
    lab: 'Omega Yeast',
    productCode: 'OYL-090',
    name: 'Espe',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The Espe kveik blend from Grodås, Norway, giving lychee, pear and tropical fruit that bolsters modern hop character. It ferments hot and fast like the rest of the family.',
    tags: ['kveik', 'espe', 'norwegian', 'lychee', 'pear', 'tropical', 'hot'],
    url: 'https://omegayeast.com/products/espe-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-091',
    lab: 'Omega Yeast',
    productCode: 'OYL-091',
    name: 'Hornindal',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'Hornindal farmhouse kveik: pineapple, stone fruit and dried fruit at the hot end of the range, much quieter if you ferment it like an ale. A favorite under tropical hops, and it drops hard enough to harvest easily.',
    tags: ['kveik', 'hornindal', 'tropical', 'pineapple', 'norwegian', 'fast'],
    url: 'https://omegayeast.com/products/hornindal-kveik-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-100',
    lab: 'Omega Yeast',
    productCode: 'OYL-100',
    name: 'Lager I',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to come from Budejovice, and the early inspiration for light American lager: crisp, balanced and lightly malty. A good default when a lager should taste of nothing in particular.',
    tags: ['lager', 'czech', 'budejovice', 'crisp', 'balanced', 'clean'],
    url: 'https://omegayeast.com/products/lager-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-101',
    lab: 'Omega Yeast',
    productCode: 'OYL-101',
    name: 'Pilsner I',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to be the H strain of the famous Pilsen brewery: dry, neutral and gently malty with a light floral note. The obvious pick for a Bohemian pilsner.',
    tags: ['lager', 'czech', 'pilsner-urquell', 'dry', 'neutral', 'floral'],
    url: 'https://omegayeast.com/products/pilsner-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-102',
    lab: 'Omega Yeast',
    productCode: 'OYL-102',
    name: 'American Pilsner',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to come from the most popular beer in America: mild, neutral and smooth with good malt character. It throws very little sulfur, which shortens the conditioning.',
    tags: ['lager', 'american', 'pilsner', 'mild', 'smooth', 'malty', 'apple'],
    url: 'https://omegayeast.com/products/american-pilsner-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-103',
    lab: 'Omega Yeast',
    productCode: 'OYL-103',
    name: 'American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The only haze-positive lager strain Omega has identified, and more flocculent and complex than its lager peers. Dry hopping it gives a hazy lager, which is a genuinely new option.',
    tags: ['lager', 'american', 'hazy', 'flocculent', 'complex', 'ipa'],
    url: 'https://omegayeast.com/products/american-lager-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-104',
    lab: 'Omega Yeast',
    productCode: 'OYL-104',
    name: 'Danish Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Crisp, dry, soft and round with good hop support, which makes it a distinctive choice for hoppy lagers and pilsners. Give it a proper lagering period before you judge it.',
    tags: ['lager', 'danish', 'crisp', 'dry', 'soft', 'hop-forward', 'pilsner'],
    url: 'https://omegayeast.com/products/danish-lager-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-105',
    lab: 'Omega Yeast',
    productCode: 'OYL-105',
    name: 'West Coast Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A lager strain that performs at ale temperatures: malty and crystal clear, and the obvious choice for California common. Many brewers use it as a shortcut to a lager without a lager fridge.',
    tags: ['california-common', 'steam', 'lager', 'warm', 'malty', 'clear'],
    url: 'https://omegayeast.com/products/west-coast-lager-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-106',
    lab: 'Omega Yeast',
    productCode: 'OYL-106',
    name: 'German Lager I',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      "Thought to be the world's most used lager strain, and it behaves like it: versatile, crisp and malty with light esters across a wide range. It will give a convincing lager at ale temperatures, but the diacetyl rest is not optional.",
    tags: ['lager', 'german', 'w34-70', 'crisp', 'malty', 'versatile', 'wide-range'],
    url: 'https://omegayeast.com/products/german-lager-i-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-107',
    lab: 'Omega Yeast',
    productCode: 'OYL-107',
    name: 'Oktoberfest',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A favorite for märzen and Oktoberfest: smooth, rich and full-bodied malt character. A thorough diacetyl rest is required, not optional.',
    tags: ['lager', 'oktoberfest', 'marzen', 'malty', 'full-bodied', 'diacetyl-rest'],
    url: 'https://omegayeast.com/products/oktoberfest-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-108',
    lab: 'Omega Yeast',
    productCode: 'OYL-108',
    name: 'Pilsner II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to be the D strain of the famous Pilsen brewery, dry and malty, and the second of the celebrated Czech pilsner pair. Slower than the German strains, and worth the wait.',
    tags: ['lager', 'czech', 'pilsner', 'dry', 'malty', 'bohemian'],
    url: 'https://omegayeast.com/products/pilsner-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-109',
    lab: 'Omega Yeast',
    productCode: 'OYL-109',
    name: 'German Lager II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A confident Bavarian fermenter with good attenuation and a smooth, full-bodied malty finish. Balanced in aroma with a slight sulfur note that conditions out.',
    tags: ['lager', 'german', 'bavarian', 'malty', 'full-bodied', 'smooth'],
    url: 'https://omegayeast.com/products/german-lager-ii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-111',
    lab: 'Omega Yeast',
    productCode: 'OYL-111',
    name: 'German Bock',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to come from Aying in Bavaria, balancing malt and hops across bocks, doppelbocks, Oktoberfest and helles. One strain covering most of the German malt-forward range.',
    tags: ['lager', 'german', 'bock', 'doppelbock', 'malty', 'versatile'],
    url: 'https://omegayeast.com/products/german-bock-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-113',
    lab: 'Omega Yeast',
    productCode: 'OYL-113',
    name: 'Mexican Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Thought to originate from a well-known Mexican brewery: clean, crisp and bright, with a wide fermentation range for a lager strain. Forgiving if the temperature drifts.',
    tags: ['lager', 'mexican', 'clean', 'crisp', 'bright', 'wide-range'],
    url: 'https://omegayeast.com/products/mexican-lager-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-114',
    lab: 'Omega Yeast',
    productCode: 'OYL-114',
    name: 'Bayern Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A clean, crisp Bavarian lager strain with a wide temperature range, strong flocculation and minimal diacetyl. It covers the whole lager range, from a modern hoppy lager to a malty one.',
    tags: ['lager', 'bavarian', 'clean', 'crisp', 'flocculent', 'wide-range', 'low-diacetyl'],
    url: 'https://omegayeast.com/products/bayern-lager-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-200',
    lab: 'Omega Yeast',
    productCode: 'OYL-200',
    name: 'Tropical IPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'Originally sold as a Brettanomyces but reclassified as a diastatic Saccharomyces, this strain throws mango and passionfruit esters and attenuates almost completely. Being STA1 positive it will keep working in package — do not bottle it alongside anything you need stable.',
    tags: ['tropical', 'diastatic', 'ipa', 'dry-finish', 'esters'],
    url: 'https://omegayeast.com/products/tropical-ipa-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-201',
    lab: 'Omega Yeast',
    productCode: 'OYL-201',
    name: 'Brett Claussenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The mildest point on the Brett spectrum: leather with some pineapple, and best used as a secondary pitch. It will not take over a beer the way lambicus does.',
    tags: ['brett', 'claussenii', 'leather', 'pineapple', 'mild', 'secondary'],
    url: 'https://omegayeast.com/products/brett-claussenii-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-202',
    lab: 'Omega Yeast',
    productCode: 'OYL-202',
    name: 'Brett Bruxellensis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'First classified in 1904, and still the reference for barnyard earthiness with a light medicinal edge. Medium intensity, which makes it the usual first Brett to try.',
    tags: ['brett', 'bruxellensis', 'barnyard', 'earthy', 'medicinal', 'secondary'],
    url: 'https://omegayeast.com/products/brett-bruxellensis-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-203',
    lab: 'Omega Yeast',
    productCode: 'OYL-203',
    name: 'Brett Lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Horsey, spicy, cherry-pie funk at real intensity, for brewers who want the Brett to be the point. Deploy it in secondary unless you want nothing else in the glass.',
    tags: ['brett', 'lambicus', 'horsey', 'cherry', 'spicy', 'secondary', 'intense'],
    url: 'https://omegayeast.com/products/brett-lambicus-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-210',
    lab: 'Omega Yeast',
    productCode: 'OYL-210',
    name: 'Brett Blend #1 Where Da Funk',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'One Brett-famous brewery strain and one Saccharomyces, giving huge tropical fruit aroma with mild funk and low acidity. It carries STA1 and attenuates past 82%.',
    tags: ['brett', 'blend', 'tropical', 'mild-funk', 'low-acid', 'sta1', 'primary'],
    url: 'https://omegayeast.com/products/brett-blend-1-where-da-funk-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-211',
    lab: 'Omega Yeast',
    productCode: 'OYL-211',
    name: "Brett Blend #2 Bit'o'Funk",
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'The Saccharomyces from Blend #1 spiked with Brettanomyces bruxellensis, for moderate funk that develops in secondary. It carries STA1.',
    tags: ['brett', 'blend', 'bruxellensis', 'moderate-funk', 'secondary', 'sta1'],
    url: 'https://omegayeast.com/products/brett-blend-2-bit-o-funk-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-212',
    lab: 'Omega Yeast',
    productCode: 'OYL-212',
    name: 'Brett Blend #3 Bring on da Funk',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'Seven strains: the Saccharomyces from Blend #1, bruxellensis, lambicus and Brett isolates from a Brett-famous brewery. The most complex blend in the range, and the slowest.',
    tags: ['brett', 'blend', 'complex', 'funk', 'lambicus', 'sta1', 'aging'],
    url: 'https://omegayeast.com/products/brett-blend-3-bring-on-da-funk-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-216',
    lab: 'Omega Yeast',
    productCode: 'OYL-216',
    name: 'Northwest Farmhouse Brett',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A Brettanomyces bruxellensis variant from a Northwest US brewery, known for white-wine character and light funk. Gentler than the classic bruxellensis and more aromatic.',
    tags: ['brett', 'bruxellensis', 'white-wine', 'light-funk', 'northwest', 'secondary'],
    url: 'https://omegayeast.com/products/northwest-farmhouse-brett-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-217',
    lab: 'Omega Yeast',
    productCode: 'OYL-217',
    name: 'C2C American Farmhouse',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'A coast-to-coast blend of a Northeast saison strain and a Northwest Brett strain, producing a dry, funky beer quickly. It carries STA1, which is why it gets there so fast.',
    tags: ['brett', 'blend', 'saison', 'farmhouse', 'dry', 'sta1', 'fast'],
    url: 'https://omegayeast.com/products/c2c-american-farmhouse-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-218',
    lab: 'Omega Yeast',
    productCode: 'OYL-218',
    name: 'All the Bretts',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      "An evolving blend of Brett strains from across Omega's collection, for high attenuation and fruity, funky character in secondary. The composition changes over time by design.",
    tags: ['brett', 'blend', 'funky', 'fruity', 'attenuative', 'sta1', 'secondary'],
    url: 'https://omegayeast.com/products/all-the-bretts-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-400',
    lab: 'Omega Yeast',
    productCode: 'OYL-400',
    name: 'Bananza',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A hefeweizen strain with phenolic expression switched off, which leaves pure banana and no clove at all. An engineered strain, and a very direct way to settle that argument.',
    tags: ['banana', 'wheat', 'hefeweizen', 'engineered', 'pof-negative', 'hazy'],
    url: 'https://omegayeast.com/products/bananza-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-401',
    lab: 'Omega Yeast',
    productCode: 'OYL-401',
    name: 'Sundew',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      "Omega's most popular Belgian strain with phenolic expression inactivated, which unmasks jammy esters underneath. Belgian fruit without Belgian spice.",
    tags: ['belgian', 'jammy', 'esters', 'engineered', 'pof-negative', 'high-gravity'],
    url: 'https://omegayeast.com/products/sundew-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-402',
    lab: 'Omega Yeast',
    productCode: 'OYL-402',
    name: 'Cosmic Punch',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A thiolized strain that unlocks bound thiols at a complementary level rather than a dominant one: grapefruit and passion fruit under the hops. Haze-positive, and built for modern IPA.',
    tags: ['thiolized', 'tropical', 'grapefruit', 'passion-fruit', 'hazy', 'ipa', 'engineered'],
    url: 'https://omegayeast.com/products/cosmic-punch-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-403',
    lab: 'Omega Yeast',
    productCode: 'OYL-403',
    name: 'Lunar Crush',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A thiol-releasing lager strain: clean lager fermentation with passionfruit and grapefruit lifted out of the hop and malt precursors. Built for cold IPA and hop-forward lagers; use a quiet hop bill if you want a classic pilsner.',
    tags: ['lager', 'thiol', 'cold-ipa', 'tropical', 'hop-forward', 'clean'],
    url: 'https://omegayeast.com/products/lunar-crush-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-404',
    lab: 'Omega Yeast',
    productCode: 'OYL-404',
    name: 'Star Party',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      "The world's most familiar ale strain with thiol unlocking added, giving a tropical dimension over Chico's clean base. Everything you expect from WLP001, plus an aroma it never had.",
    tags: ['thiolized', 'chico', 'tropical', 'clean', 'neutral', 'engineered', 'ipa'],
    url: 'https://omegayeast.com/products/star-party-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-405',
    lab: 'Omega Yeast',
    productCode: 'OYL-405',
    name: 'Helio Gazer',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A thiol-releasing hazy-IPA strain: guava and grapefruit on top of the usual stone-fruit esters, so a modest hop bill still reads tropical. Pair it with thiol-rich hops and a light kettle bitterness.',
    tags: ['thiol', 'hazy', 'ipa', 'tropical', 'biotransformation', 'guava'],
    url: 'https://omegayeast.com/products/helio-gazer-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-406',
    lab: 'Omega Yeast',
    productCode: 'OYL-406',
    name: 'Little DIPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      "The DIPA strain's thiolized evolution, with its peach-ring and apricot character opened further by tropical expression. Diacetyl production is knocked out at the source.",
    tags: ['thiolized', 'conan', 'peach', 'apricot', 'tropical', 'engineered', 'dko'],
    url: 'https://omegayeast.com/products/little-dipa-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-407',
    lab: 'Omega Yeast',
    productCode: 'OYL-407',
    name: 'Vossa Nova',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 12, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      "Voss kveik's temperature range and speed, thiolized: tropical aromatics unlocked across the full band from 12 °C to 35 °C. A fast beer that smells like a slow one.",
    tags: ['kveik', 'voss', 'thiolized', 'tropical', 'fast', 'wide-range', 'engineered'],
    url: 'https://omegayeast.com/products/vossa-nova-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-408',
    lab: 'Omega Yeast',
    productCode: 'OYL-408',
    name: 'DayBreak-V',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'All the juicy hop-boosting character of British V with lager-like clarity, using Lumina technology to remove yeast-derived haze. Hazy-IPA flavor in a bright beer.',
    tags: ['british', 'hazy', 'ipa', 'lumina', 'clear', 'engineered', 'juicy'],
    url: 'https://omegayeast.com/products/daybreak-v-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-409',
    lab: 'Omega Yeast',
    productCode: 'OYL-409',
    name: 'Tartango',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 12, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Lactic acid produced during fermentation with no bacteria and no kettle souring, with acidity proportional to the pitch. It ferments across the kveik temperature range, so a sour beer can be ready in days.',
    tags: ['sour', 'lactic', 'engineered', 'kettle-sour-free', 'kveik', 'fast', 'wide-range'],
    url: 'https://omegayeast.com/products/tartango-next',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-430',
    lab: 'Omega Yeast',
    productCode: 'OYL-430',
    name: 'West Coast I+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Chico with diacetyl production knocked out at the source and Lumina for crystal clarity. The workhorse strain with its two most common complaints engineered away.',
    tags: ['chico', 'american', 'dko', 'lumina', 'clear', 'engineered', 'ipa'],
    url: 'https://omegayeast.com/products/west-coast-i-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-431',
    lab: 'Omega Yeast',
    productCode: 'OYL-431',
    name: 'British V+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The gold standard for hazy IPA, engineered for production demands: reliably hazy, juicy and hop-boosting. Consistent in a way the wild-type London strains are not.',
    tags: ['british', 'hazy', 'neipa', 'juicy', 'engineered', 'ipa', 'flocculent'],
    url: 'https://omegayeast.com/products/british-v-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-432',
    lab: 'Omega Yeast',
    productCode: 'OYL-432',
    name: 'Extra Special+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Built for crystal-clear IPAs and classic English styles: it drops brilliantly, crops easily, and diacetyl is knocked out at the source. A modern take on the ESB strain.',
    tags: ['english', 'esb', 'clear', 'flocculent', 'dko', 'engineered', 'ipa'],
    url: 'https://omegayeast.com/products/extra-special-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-433',
    lab: 'Omega Yeast',
    productCode: 'OYL-433',
    name: 'Bayern Lager+',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A versatile Bavarian lager strain in its engineered form, keeping the traditional clean and crisp character. A broad base for most German lager styles.',
    tags: ['lager', 'bavarian', 'clean', 'crisp', 'engineered', 'versatile'],
    url: 'https://omegayeast.com/products/bayern-lager-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-434',
    lab: 'Omega Yeast',
    productCode: 'OYL-434',
    name: 'Point Loma+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Engineered for West Coast IPA: neutral, versatile and flocculent, with diacetyl knocked out and Lumina working on clarity. Bright beer without a fining.',
    tags: ['american', 'west-coast', 'neutral', 'flocculent', 'dko', 'lumina', 'engineered'],
    url: 'https://omegayeast.com/products/point-loma-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-435',
    lab: 'Omega Yeast',
    productCode: 'OYL-435',
    name: 'Kolsch II+',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A modern kölsch strain that pulls double duty: lightly fruity, crisp and hop-accentuating, and genetically able to go hazy when dry hopped. One strain for a kölsch and for a pale hazy beer.',
    tags: ['kolsch', 'german', 'crisp', 'clean', 'hazy', 'engineered', 'dual-purpose'],
    url: 'https://omegayeast.com/products/kolsch-ii-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-436',
    lab: 'Omega Yeast',
    productCode: 'OYL-436',
    name: 'DIPA+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      "Conan's peach-ring and apricot character modernised for hop-forward programs, with diacetyl knocked out at the source. The same aroma, less of the rest.",
    tags: ['conan', 'peach', 'apricot', 'dko', 'engineered', 'ipa', 'hop-forward'],
    url: 'https://omegayeast.com/products/dipa-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-437',
    lab: 'Omega Yeast',
    productCode: 'OYL-437',
    name: 'German Lager I+',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      "One of the world's most prominent lager strains, updated for modern production: spectacularly bright and clean across the range. The engineered answer to 34/70.",
    tags: ['lager', 'german', 'bright', 'clean', 'engineered', 'versatile'],
    url: 'https://omegayeast.com/products/german-lager-i-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-438',
    lab: 'Omega Yeast',
    productCode: 'OYL-438',
    name: 'French Saison+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'The French saison strain with STA1 activity eliminated at the source, so the lemon, pear and pepper come without the cross-contamination risk. Farmhouse character you can keep in a shared brewery.',
    tags: ['saison', 'french', 'lemon', 'pear', 'pepper', 'sta1-free', 'engineered'],
    url: 'https://omegayeast.com/products/french-saison-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-439',
    lab: 'Omega Yeast',
    productCode: 'OYL-439',
    name: 'Belgian Saison II+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'Belgian Saison II with STA1 activity eliminated, keeping the earthy, spicy farmhouse complexity without the diastaticus problem. It attenuates less far than the wild type, by design.',
    tags: ['saison', 'belgian', 'earthy', 'spicy', 'sta1-free', 'engineered', 'farmhouse'],
    url: 'https://omegayeast.com/products/belgian-saison-ii-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-440',
    lab: 'Omega Yeast',
    productCode: 'OYL-440',
    name: 'American Saison+',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'An American saison strain with STA1 activity eliminated at the source, giving clean, expressive farmhouse character with no diastaticus risk. Safe for a brewery that also packages lager.',
    tags: ['saison', 'american', 'farmhouse', 'clean', 'sta1-free', 'engineered'],
    url: 'https://omegayeast.com/products/american-saison-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-441',
    lab: 'Omega Yeast',
    productCode: 'OYL-441',
    name: 'Mexican Lager +',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'Clean, crisp, bright and versatile, with diacetyl knocked out. A neutral lager base that needs less conditioning than the strain it came from.',
    tags: ['lager', 'mexican', 'clean', 'crisp', 'dko', 'engineered', 'neutral'],
    url: 'https://omegayeast.com/products/mexican-lager-plus',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-500',
    lab: 'Omega Yeast',
    productCode: 'OYL-500',
    name: 'Saisonstein',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'A bred hybrid of a classic saison strain and a reliable ale yeast: it keeps the peppery phenols and very dry finish but ferments out without the notorious mid-gravity stall. It is diastatic, so keep it away from shared packaging lines and non-saison equipment.',
    tags: ['saison', 'farmhouse', 'phenolic', 'dry-finish', 'diastatic'],
    url: 'https://omegayeast.com/products/saisonstein-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-501',
    lab: 'Omega Yeast',
    productCode: 'OYL-501',
    name: 'Gulo',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 100 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: true,
    description:
      'A patented hybrid of Irish ale and French saison: it finishes as dry as a saison without the stall, and throws a light pepper note rather than a full farmhouse phenol. Diastatic — keep it away from beers you need stable in package.',
    tags: ['saison', 'hybrid', 'diastatic', 'dry-finish', 'irish', 'farmhouse'],
    url: 'https://omegayeast.com/products/gulo-ale-standard',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-oyl-605',
    lab: 'Omega Yeast',
    productCode: 'OYL-605',
    name: 'Lacto',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 20, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A blend of Lactobacillus strains for souring wort before the boil, dropping pH into the 3.2–3.5 region in a day or two when held warm. It produces lactic acid rather than alcohol, so the attenuation figure here is nominal — pitch a real yeast afterwards to ferment the beer.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse'],
    url: 'https://omegayeast.com/products/lacto-standard',
    provenance: 'representative',
  },
  {
    id: 'omega-oyl-606',
    lab: 'Omega Yeast',
    productCode: 'OYL-606',
    name: 'Pediococcus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A modestly hop-tolerant Pediococcus producing a clean lactic tang over time. Pair it with Brett, which will clean up the diacetyl it leaves behind.',
    tags: ['pedio', 'pediococcus', 'sour', 'lactic', 'hop-tolerant', 'brett', 'aging'],
    url: 'https://omegayeast.com/products/pediococcus-standard',
    provenance: 'representative',
  },
  {
    id: 'omega-oyl-999',
    lab: 'Omega Yeast',
    productCode: 'OYL-999',
    name: 'Pink Boots Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A versatile lager strain giving a bright, crisp fermentation on a relatively tight timeline across a wide temperature range. Fifteen percent of each sale goes to Pink Boots Society scholarships.',
    tags: ['lager', 'versatile', 'crisp', 'bright', 'hop-forward', 'wide-range', 'pink-boots'],
    url: 'https://omegayeast.com/products/pink-boots-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-pin-001dry',
    lab: 'Omega Yeast',
    productCode: 'PIN-001DRY',
    name: 'Heritage American Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'An active dry American ale yeast producing well-balanced beers with low diacetyl and moderate flocculation. A shelf-stable stand-in for a liquid Chico pitch.',
    tags: ['dry', 'american', 'clean', 'balanced', 'low-diacetyl', 'flocculent'],
    url: 'https://omegayeast.com/products/pinnacle-heritage-american-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-pin-002dry',
    lab: 'Omega Yeast',
    productCode: 'PIN-002DRY',
    name: 'Heritage English Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'An active dry English ale yeast giving fruity, estery beers with a clean finish that lets malt and hops through. It flocculates hard and goes hazy when dry hopped.',
    tags: ['dry', 'english', 'fruity', 'estery', 'clean-finish', 'flocculent', 'hazy'],
    url: 'https://omegayeast.com/products/heritage-english-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-pin-003dry',
    lab: 'Omega Yeast',
    productCode: 'PIN-003DRY',
    name: 'Heritage Pilsner',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 12, max: 15 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'An active dry pilsner yeast producing well-attenuated, clean and crisp lagers. It works at proper lager temperatures rather than the wide bands some dry lager yeasts claim.',
    tags: ['dry', 'lager', 'pilsner', 'clean', 'crisp', 'attenuative'],
    url: 'https://omegayeast.com/products/heritage-pilsner',
    provenance: 'spec-sheet',
  },
  {
    id: 'omega-pin-004dry',
    lab: 'Omega Yeast',
    productCode: 'PIN-004DRY',
    name: 'Crisp Sour',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 8,
    sta1: false,
    description:
      'An active dry Lachancea thermotolerans that produces lactic acid as it ferments, so a sour beer needs no bacteria and no kettle souring. It runs warm, up to 30 °C.',
    tags: ['dry', 'sour', 'lachancea', 'lactic', 'kettle-sour-free', 'warm'],
    url: 'https://omegayeast.com/products/pinnacle-crisp-sour',
    provenance: 'spec-sheet',
  },
];
